import { useState, useEffect, useCallback } from "react";
import { Moon, Crown, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import BottomNav from "@/components/BottomNav";
import TodayView from "@/components/TodayView";
import ShiftCalendar from "@/components/ShiftCalendar";
import EarningsView from "@/components/EarningsView";
import SettingsView from "@/components/SettingsView";
import UpgradeBanners from "@/components/UpgradeBanners";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Dashboard() {
  const { t, setLang } = useI18n();
  const { isPro, openPaywall } = useSubscription();
  const [tab, setTab] = useState("today");
  const [settings, setSettings] = useState(null);

  const loadSettings = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    if (data.language) setLang(data.language);
  }, [setLang]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-white/10">
        <div className="max-w-lg mx-auto flex items-center justify-between px-5 py-3.5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Moon className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-display text-lg font-extrabold">{t("brand")}</span>
          </div>
          <div className="flex items-center gap-2">
            {isPro ? (
              <span data-testid="header-pro-badge" className="flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-bold text-primary">
                <Crown className="h-3.5 w-3.5" /> {t("pro")}
              </span>
            ) : (
              <button data-testid="header-upgrade-btn" onClick={openPaywall}
                className="flex items-center gap-1 rounded-full border border-primary/40 px-2.5 py-1 text-[11px] font-bold text-primary hover:bg-primary/10 transition-colors">
                <Sparkles className="h-3.5 w-3.5" /> {t("unlock")}
              </button>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-5 py-5">
        <UpgradeBanners />
        {tab === "today" && <TodayView settings={settings} />}
        {tab === "calendar" && <ShiftCalendar settings={settings} />}
        {tab === "earnings" && <EarningsView settings={settings} />}
        {tab === "settings" && <SettingsView settings={settings} onSaved={loadSettings} />}
      </main>

      <BottomNav tab={tab} setTab={setTab} />
    </div>
  );
}
